import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Pop-out van het contextpaneel naar een tweede scherm.
 *
 * Gebruikt Document Picture-in-Picture waar beschikbaar (Chrome/Edge: altijd-bovenop,
 * naar scherm 2 te slepen), anders een gewoon browservenster (window.open).
 *
 * Het paneel wordt door de aanroeper via createPortal in `popoutWin.document.body`
 * gerenderd → zelfde JS-context, dus store + handlers (bv. "Gebruik in offerte") werken
 * direct, zonder cross-window messaging. Alleen HTML5-drag-and-drop kan de schermrand
 * niet over (daarvoor is de "Gebruik"-klik bedoeld).
 */
function syncStyles(srcDoc, targetDoc) {
  // Bestaande overgenomen stijlen verwijderen en opnieuw klonen (HMR/laat geladen CSS).
  targetDoc.querySelectorAll('style[data-orion-popout], link[data-orion-popout]').forEach((n) => n.remove())
  for (const node of srcDoc.querySelectorAll('style, link[rel="stylesheet"]')) {
    const clone = node.cloneNode(true)
    clone.setAttribute('data-orion-popout', '1')
    targetDoc.head.appendChild(clone)
  }
  // Dark mode + basisopmaak overnemen zodat het paneel er identiek uitziet.
  targetDoc.documentElement.className = srcDoc.documentElement.className
  targetDoc.documentElement.style.height = '100%'
  targetDoc.body.className = srcDoc.body.className
  targetDoc.body.style.margin = '0'
  targetDoc.body.style.height = '100%'
}

export function usePanelPopout() {
  const [popoutWin, setPopoutWin] = useState(null)
  const winRef = useRef(null)

  const closePopout = useCallback(() => {
    const w = winRef.current
    if (w && !w.closed) {
      try { w.close() } catch { /* PiP/venster al weg */ }
    }
    winRef.current = null
    setPopoutWin(null)
  }, [])

  const openPopout = useCallback(async () => {
    if (winRef.current && !winRef.current.closed) {
      try { winRef.current.focus() } catch { /* PiP heeft geen focus() */ }
      return
    }
    let w = null
    try {
      if (window.documentPictureInPicture?.requestWindow) {
        w = await window.documentPictureInPicture.requestWindow({ width: 440, height: 840 })
      }
    } catch {
      w = null
    }
    if (!w) {
      try {
        w = window.open('', 'orion-paneel', 'width=440,height=880,menubar=no,toolbar=no,location=no,status=no')
      } catch {
        w = null
      }
    }
    if (!w) {
      // eslint-disable-next-line no-alert
      alert('Kon geen tweede venster openen. Sta pop-ups toe voor Orion, of gebruik Chrome/Edge voor het zwevende paneel.')
      return
    }
    try { w.document.title = 'Orion — paneel' } catch { /* noop */ }
    syncStyles(document, w.document)

    const onClose = () => { winRef.current = null; setPopoutWin(null) }
    w.addEventListener('pagehide', onClose)
    w.addEventListener('unload', onClose)
    winRef.current = w
    setPopoutWin(w)
  }, [])

  // Sluit het externe venster wanneer de host-component unmount.
  useEffect(() => () => {
    const w = winRef.current
    if (w && !w.closed) { try { w.close() } catch { /* noop */ } }
  }, [])

  return { popoutWin, isPopped: !!popoutWin, openPopout, closePopout }
}

export default usePanelPopout
